<template>
  <v-card class="pa-4">
    <v-toolbar flat>
      <v-toolbar-title>{{ $i18n.translate("VIN Transaction History") }}</v-toolbar-title>
      <v-spacer />
      <v-text-field
        id="search"
        v-model="vin"
        label="Search VIN"
        autocomplete="off"
        flat
        solo-inverted
        hide-details
        clearable
        clear-icon="mdi-close-circle-outline"
        style="max-width: 300px"
        @keyup.enter="onSubmit"
        @click:clear="onClear"
      />
    </v-toolbar>
    <div class="px-4 pb-2 text-body-1 text--secondary">
      {{ $i18n.translate("Enter a VIN Number to review transaction history.") }}
    </div>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
    <div v-if="vinNotFound" class="text-center mt-4">
      {{ $i18n.translate("No data found for the entered VIN") }}
    </div>
    <v-card-text>
      <v-card v-if="vinInfo" class="mb-4 elevation-2">
        <v-card-title>
          <b>{{ $i18n.translate("VIN Details") }}</b>
        </v-card-title>

        <v-card-text>
          <v-data-table
            :headers="vinHeaders"
            :items="[vinInfo]"
            hide-default-footer
            disable-pagination
            class="elevation-0"
          >
            <template v-slot:item.productFields="">
              <span v-for="(field, index) in productCustomFields" :key="index">
                <b>{{ field.name }}</b
                >: {{ productCustomFieldRow[`field${index + 1}`] }}<br />
              </span>
            </template>
            <template v-slot:item.expirationDate="{ item }">
              <span v-if="item.expirationDate">
                {{ item.expirationDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
              </span>
            </template>
            <template v-slot:item.effective="{ item }">
              <v-chip color="primary" v-if="item.effective">{{ $i18n.translate("Active") }}</v-chip>
              <v-chip color="danger" v-else>{{ $i18n.translate("Inactive") }}</v-chip>
            </template>
            <template v-slot:item.updatedDate="{ item }">
              <span v-if="item.updatedDate">
                {{ item.updatedDate | formatDateFromNow }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
      <div v-if="noTransactionsFound" class="text-center mt-4 text-body-1">
        {{ $i18n.translate("VIN was found, but no transaction history is available.") }}
      </div>
      <v-expansion-panels v-if="payoutRows.length > 0" class="mt-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <b>{{ $i18n.translate("Dealer Transaction Details") }} ({{ dealerTransactions.length }})</b>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
              :headers="dealerHeaders"
              :items="dealerTransactions"
              hide-default-footer
              disable-pagination
              class="elevation-0"
            >
              <template v-slot:no-data>
                {{ $i18n.translate("No dealer transactions found.") }}
              </template>

              <template v-slot:item.amount="{ item }">
                <span v-if="item.amount">
                  {{ item.amount | toNumber(2) }}
                  {{ item.currency ? item.currency?.name : "" }}
                </span>
              </template>

              <template v-slot:item.transactionDate="{ item }">
                <span v-if="item.transactionDate">
                  {{ item.transactionDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
                </span>
              </template>
              <template v-slot:item.payout="{ item }">
                <PayoutStatusChipField :payout="item.payout" />
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            <b>{{ $i18n.translate("Salesperson Transaction Details") }} ({{ salespersonTransactions.length }})</b>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
              :headers="salespersonHeaders"
              :items="salespersonTransactions"
              hide-default-footer
              disable-pagination
              class="elevation-0"
            >
              <template v-slot:no-data>
                {{ $i18n.translate("No salesperson transactions found.") }}
              </template>

              <template v-slot:item.amount="{ item }">
                <span v-if="item.amount">
                  {{ item.amount | toNumber(2) }}
                  {{ item.currency ? item.currency?.name : "" }}
                </span>
              </template>

              <template v-slot:item.transactionDate="{ item }">
                <span v-if="item.transactionDate">
                  {{ item.transactionDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
                </span>
              </template>
              <template v-slot:item.payout="{ item }">
                <PayoutStatusChipField :payout="item.payout" />
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            <b>{{ $i18n.translate("Racer Transaction Details") }} ({{ racerTransactions.length }})</b>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
              :headers="racerHeaders"
              :items="racerTransactions"
              hide-default-footer
              disable-pagination
              class="elevation-0"
            >
              <template v-slot:no-data>
                {{ $i18n.translate("No racer transactions found.") }}
              </template>

              <template v-slot:item.amount="{ item }">
                <span v-if="item.amount">
                  {{ item.amount | toNumber(2) }}
                  {{ item.currency ? item.currency?.name : "" }}
                </span>
              </template>

              <template v-slot:item.transactionDate="{ item }">
                <span v-if="item.transactionDate">
                  {{ item.transactionDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
                </span>
              </template>
              <template v-slot:item.payout="{ item }">
                <PayoutStatusChipField :payout="item.payout" />
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script>
import ApiService from "@/gapp-components/services/api.service";
import UtilService from "@/gapp-components/services/util.service";
import { mapGetters } from "vuex";
import PayoutStatusChipField from "@/gapp-components/components/fields/PayoutStatusChipField.vue";

export default {
  name: "vinTransactionHistory",
  components: {
    PayoutStatusChipField
  },
  metaInfo: {
    title: "VIN Transaction History"
  },

  data() {
    return {
      vin: "",
      vinInfo: null,
      serialNumberEntity: null,
      loading: false,
      sales: [],
      payouts: [],
      payoutRows: [],
      payoutTotal: 0,
      vinNotFound: false,
      noTransactionsFound: false,
      options: {
        page: 1,
        itemsPerPage: 100
      },
      vinHeaders: [
        { text: this.$i18n.translate("Product"), value: "modelCode", sortable: false },
        { text: this.$i18n.translate("Name"), value: "productName", sortable: false },
        { text: this.$i18n.translate("Serial #"), value: "serialNumber", sortable: false },
        { text: this.$i18n.translate("Product Fields"), value: "productFields", sortable: false },
        { text: this.$i18n.translate("Expiration Date"), value: "expirationDate", sortable: false },
        { text: this.$i18n.translate("Status"), value: "effective", align: "center", sortable: false },
        { text: this.$i18n.translate("Last Updated"), value: "updatedDate", sortable: false }
      ],
      baseHeaders: [
        { text: this.$i18n.translate("Name"), value: "name" },
        { text: this.$i18n.translate("Dealer Number"), value: "dealerNumber" },
        { text: this.$i18n.translate("Dealer Name"), value: "dealerName" },
        { text: this.$i18n.translate("Promotion"), value: "promotion" },
        { text: this.$i18n.translate("Amount"), value: "amount" },
        { text: this.$i18n.translate("Transaction Date"), value: "transactionDate" },
        { text: this.$i18n.translate("Payment Status"), value: "payout", sortable: false }
      ],
      dealerPrincipalCache: {}
    };
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram"]),
    productCustomFields() {
      return (this.selectedProgram?.programGroup?.productCustomFields || []).slice(0, 10);
    },

    dealerHeaders() {
      return [{ text: "Dealer Principal ID", value: "salespersonId" }, ...this.baseHeaders];
    },

    salespersonHeaders() {
      return [{ text: "Salesperson ID", value: "salespersonId" }, ...this.baseHeaders];
    },

    racerHeaders() {
      return [{ text: "Racer ID", value: "racerId" }, ...this.baseHeaders];
    },

    dealerTransactions() {
      return this.payoutRows.filter(row => row.type === "dealer");
    },

    salespersonTransactions() {
      return this.payoutRows.filter(row => row.type === "salesperson");
    },

    racerTransactions() {
      return this.payoutRows.filter(row => row.type === "racer");
    },
    productCustomFieldRow() {
      const product = this.serialNumberEntity?.product || {};
      const row = {};
      this.productCustomFields.forEach((field, index) => {
        const rawValue = product[`customFieldValue${index + 1}`] ?? "";
        row[`field${index + 1}`] = this.formatCustomFieldValue(rawValue, field);
      });
      return row;
    }
  },

  methods: {
    formatCustomFieldValue(value, customField) {
      const formatter = this.$options.filters?.customField;
      if (typeof formatter !== "function") return value;
      return formatter(value, customField);
    },
    resetState() {
      this.vinInfo = null;
      this.serialNumberEntity = null;
      this.sales = [];
      this.payouts = [];
      this.payoutRows = [];
      this.payoutTotal = 0;
      this.dealerPrincipalCache = {};
      this.vinNotFound = false;
      this.noTransactionsFound = false;
    },
    async onSubmit() {
      this.options.page = 1;
      await this.fetchData();
    },

    onClear() {
      this.vin = "";
      this.resetState();
    },

    async fetchDealerPrincipal(organization) {
      if (!organization?.id) return null;
      const cacheKey = organization.id;
      if (this.dealerPrincipalCache[cacheKey] !== undefined) return this.dealerPrincipalCache[cacheKey];

      const params = new URLSearchParams();
      params.append("size", "1");
      params.append("expand", "participantType");

      const payload = {
        organization: { id: organization.id },
        participantTypeKey: "240"
      };

      try {
        const response = await ApiService.post(`/api/participants/search?${params.toString()}`, payload);
        const principal = response?.data?.content?.[0] || null;
        this.dealerPrincipalCache[cacheKey] = principal;
        return principal;
      } catch (error) {
        this.dealerPrincipalCache[cacheKey] = null;
        return null;
      }
    },

    async fetchPayouts() {
      const salesPayouts = [];
      const customFieldPayouts = [];
      const params = UtilService.buildPaginationParams(this.options);
      params.append("expand", "promotion");
      params.append("expand", "sale");
      params.append("expand", "saleProduct.product");
      params.append("expand", "participant");
      params.append("expand", "participant.participantType");
      params.append("expand", "participant.organization");
      params.append("expand", "organization");
      params.append("expand", "organization.organizationType");

      const queryString = params.toString();
      if (this.sales.length) {
        const salesPayload = {
          sales: this.sales.map(s => ({ id: s.id })),
          payoutStatus: "all"
        };
        const salesResponse = await ApiService.post(`/api/payouts/search?${queryString}`, salesPayload);
        if (salesResponse?.data?.content) salesPayouts.push(...salesResponse.data.content);
      }

      if (this.vin) {
        const customFieldPayload = {
          payoutCustomFieldValues: {
            customFieldValue8: this.vin
          },
          payoutStatus: "all"
        };
        const customFieldResponse = await ApiService.post(`/api/payouts/search?${queryString}`, customFieldPayload);
        if (customFieldResponse?.data?.content) customFieldPayouts.push(...customFieldResponse.data.content);
      }

      const combinedPayouts = [...salesPayouts, ...customFieldPayouts];
      const uniquePayouts = [];
      const seenPayoutIds = new Set();
      for (const payout of combinedPayouts) {
        if (!seenPayoutIds.has(payout.id)) {
          seenPayoutIds.add(payout.id);
          uniquePayouts.push(payout);
        }
      }
      this.payouts = uniquePayouts;

      const payoutRows = await Promise.all(
        this.payouts.map(async p => {
          const participant = p.participant || {};
          const org = p.organization || {};

          let type = "";
          if (participant?.participantType?.participantTypeKey === "300") type = "salesperson";
          else if (participant?.participantType?.participantTypeKey === "400") type = "racer";
          else if (org?.organizationType?.organizationTypeKey === "DEALER") type = "dealer";

          let salespersonId = participant.participantKey;
          let name = participant.fullName;

          if (type === "dealer") {
            const dealerOrg = org.id ? org : participant.organization;
            const principal = await this.fetchDealerPrincipal(dealerOrg);
            if (principal) {
              salespersonId = principal.participantKey;
              name = principal.fullName;
            } else {
              salespersonId = "";
            }
          }

          return {
            type,
            id: p.id,
            salespersonId,
            racerId: participant.participantKey,
            name,
            dealerNumber: org.legacyOrganizationKey || participant.organization?.legacyOrganizationKey,
            dealerName: org.name || participant.organization?.name,
            promotion: p.promotion?.promotionKey,
            amount: p.payoutAmount,
            transactionDate:
              !p.bankTransaction?.pending && !p.bankTransaction?.voided ? p.bankTransaction?.issuedDate : "",
            payout: p
          };
        })
      );

      this.payoutRows = payoutRows;
    },

    async fetchData() {
      const vin = this.vin?.trim();
      if (!vin) return;

      this.loading = true;
      this.resetState();

      try {
        const serialRes = await ApiService.post("/api/productSerialNumbers/search?page=0&size=1", {
          productSerialNumberKey: vin
        });
        const serial = serialRes?.data?.content?.[0];
        if (!serial) {
          this.vinNotFound = true;
          return;
        }
        this.serialNumberEntity = serial;
        this.vinInfo = {
          serialNumber: serial.productSerialNumberKey,
          productName: serial.product?.name,
          modelCode: serial.product?.productKey,
          expirationDate: serial.expirationDate,
          effective: serial.effective,
          updatedDate: serial.updatedDate
        };
        const salesRes = await ApiService.post("/api/sales/search", {
          serialNumber: vin
        });
        this.sales = salesRes?.data?.content || [];
        await this.fetchPayouts();
        if (!this.payoutRows.length) {
          this.noTransactionsFound = true;
          return;
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
